import { useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { Button } from './Button.js';
import { Dialog } from './Dialog.js';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Sentence explaining what confirming actually does. */
  description?: string;
  /** Optional extra content rendered above the actions (a warning, a list of affected items). */
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Renders the confirm action in the destructive style (docs/UI_GUIDELINES.md §6). */
  destructive?: boolean;
  /** External pending state (e.g. a mutation that another control also drives). */
  isPending?: boolean;
  /**
   * Runs the confirmed action. If it returns a promise, the dialog stays
   * open — with both actions disabled — until that promise settles, and
   * closes only on success; a rejection leaves the dialog open so the
   * caller's error surface is visible in context. A synchronous
   * `() => void` closes immediately, which is the right behavior for a
   * caller that navigates away or renders its own confirmation elsewhere.
   */
  onConfirm: () => void | Promise<unknown>;
}

/**
 * The one confirmation dialog for destructive or otherwise irreversible
 * actions (APP_SPEC §36 "confirm destructive actions"). Deliberately
 * generic — title, description, and confirm label are the caller's — so
 * every such prompt in the app shares one set of focus, escape, labelling,
 * and double-submission semantics instead of each feature growing its own
 * slightly different modal.
 *
 * Double submission is impossible while a promise-returning `onConfirm` is
 * in flight: the confirm button is disabled by its own busy state and the
 * guard below rejects a second call outright, so neither a second click nor
 * a keyboard repeat can issue the request twice.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  isPending = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const busy = isSubmitting || isPending;

  const awaitConfirm = async (result: Promise<unknown>) => {
    setIsSubmitting(true);
    try {
      await result;
      onOpenChange(false);
    } catch {
      // The caller renders the failure (its mutation's `isError` state);
      // this only has to stop the dialog from closing on a failed action.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (isSubmitting) return;
    const result = onConfirm();
    if (!(result instanceof Promise)) {
      onOpenChange(false);
      return;
    }
    void awaitConfirm(result);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Escape / overlay / close-button dismissal must not abandon an
        // action that is already in flight.
        if (busy && !next) return;
        onOpenChange(next);
      }}
      title={title}
      description={description}
    >
      {children}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? 'danger' : 'primary'}
          isLoading={busy}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}

type TriggerButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'children' | 'className' | 'disabled' | 'onClick' | 'type'
> & {
  [key: `data-${string}`]: string | undefined;
};

export interface ConfirmActionButtonProps extends Omit<
  ConfirmDialogProps,
  'open' | 'onOpenChange'
> {
  /** The trigger's visible text. */
  triggerLabel: ReactNode;
  /** Accessible name for the trigger when `triggerLabel` is not self-describing. */
  triggerAriaLabel?: string;
  triggerVariant?: 'secondary' | 'danger' | 'ghost';
  triggerClassName?: string;
  triggerButtonProps?: TriggerButtonProps;
  disabled?: boolean;
}

/**
 * A trigger button wired to its own `ConfirmDialog` — the shape almost
 * every destructive control in the app wants (remove friend, block user,
 * resign, clear notifications), so those callers do not each repeat the
 * open/close state.
 */
export function ConfirmActionButton({
  triggerLabel,
  triggerAriaLabel,
  triggerVariant = 'danger',
  triggerClassName,
  triggerButtonProps,
  disabled = false,
  ...dialogProps
}: ConfirmActionButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant={triggerVariant}
        size="sm"
        disabled={disabled}
        aria-label={triggerAriaLabel}
        className={triggerClassName}
        onClick={() => setOpen(true)}
        {...triggerButtonProps}
      >
        {triggerLabel}
      </Button>
      <ConfirmDialog {...dialogProps} open={open} onOpenChange={setOpen} />
    </>
  );
}
